import type { GameDefinition, GameSceneHandle } from "../composition/GameDefinition";
import { installVConsole } from "../dev/installVConsole";
import { createBootConfig, type BootConfigDefaults, type BootEnv } from "./createBootConfig";
import { startGame } from "./startGame";

/**
 * The one game-facing shell entry (plan §2.2, decision 40): a title's `main.ts` is
 * `launchGame(definition, import.meta.env, defaults)`. It installs the staging console when the env and
 * the page say so (`installVConsole`; before the boot boundary, which it never reaches: a failed install
 * is warned and skipped), then boots `definition` into the page's `#game` element (the base page the
 * title's Vite plugin writes, `@slot/tooling/vite`) through `startGame`, which runs `createBootConfig`
 * inside its boundary (decision 31). The version is the definition's, so a title states it once.
 */
export async function launchGame<TScene extends GameSceneHandle>(definition: GameDefinition<TScene>, env: BootEnv, defaults: BootConfigDefaults): Promise<void> {
  const host = document.getElementById("game");
  if (!host) throw new Error("#game host element missing");
  const search = window.location.search;
  await installVConsole({ env, search });
  await startGame(host, definition, () => createBootConfig({ env, search, gameVersion: definition.version, defaults }));
}
